import React, { Suspense, useEffect, useState } from "react";
import { CssBaseline } from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import SidebarLayout from "./components/SidebarLayout";
import "./App.css";
import CreateCompanyForm from "./pages/admin/CreateCompanyForm";
import Loader from "./components/Loader";
import { toast, Toaster } from "sonner";
import SharedWebCall from "./pages/SharedWebCall";
import AllRoutes from "./routes/allRoutes";
import { fetchAndCacheDefaultAvatar } from "./pages/utils/avatarCache";
import VerifyEmail from "./pages/Verify";
import Cookies from "js-cookie";
import api from "./api";

// Lazy-loaded pages for auth
const Login = React.lazy(() => import("./pages/Login"));
const Registration = React.lazy(() => import("./pages/Registration"));
const ForgetPassword = React.lazy(() => import("./pages/ForgetPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const baseUrl = process.env.REACT_APP_AUTH_URL;
const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

function App() {
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);

  const refreshLocalStorageData = () => {
    const tokenData = localStorage.getItem("token");
    setToken(tokenData ? JSON.parse(tokenData).token : null);
    setUserRole(localStorage.getItem("Role"));
    setOrganizationId(localStorage.getItem("Organization"));
    setSidebarOpen(localStorage.getItem("sidebarOpen") === "true");
  };

  const setTokenAndPersist = (newToken) => {
    setToken(newToken);
    const tokenObject = { token: newToken };
    localStorage.setItem("token", JSON.stringify(tokenObject));
    sessionStorage.setItem("token", JSON.stringify(tokenObject));
  };

  const fetchAndStoreMaintenanceRoutes = async () => {
    try {
      const maintenanceRoutes = Cookies.get("maintenance_routes");
      const parsedMaintenanceRoutes = maintenanceRoutes ? JSON.parse(maintenanceRoutes) : [];
  
      if (!parsedMaintenanceRoutes || parsedMaintenanceRoutes.length === 0) {
        const { data: { data } } = await api.get(`/v1/actions/get-maintenance-flags`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        });
  
        if (data) {
          Cookies.set("maintenance_routes", JSON.stringify(data), { expires: 1 });
        }
      }
    } catch (error) {
      console.error('Error fetching maintenance routes:', error);
    }
  };
  

  const initializeApp = async () => {
    try {
      await fetchAndStoreMaintenanceRoutes();

      const cookieSessionId = Cookies.get("SessionID");
      const localSessionId = localStorage.getItem("SessionID");

      // ❌ Session mismatch — clear everything and logout
      if (localSessionId && cookieSessionId && localSessionId !== cookieSessionId) {
        console.warn("Session mismatch — logging out");
        localStorage.clear();
        sessionStorage.clear();

        // Expire all cookies
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; path=/; domain=.liaplus.com; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=None`;
        });

        window.location.href = "/login";
        return;
      }

      const cookieToken = Cookies.get("token");
      const cookiesSynced = localStorage.getItem("CookiesSynced");

      // ✅ Only sync cookies once per session
      if (cookieToken && !cookiesSynced) {
        setTokenAndPersist(cookieToken);

        const cookieKeys = ["UserId", "Role", "Organization", "User", "sidebarOpen", "SessionID"];
        cookieKeys.forEach((key) => {
          const val = Cookies.get(key);
          if (val) localStorage.setItem(key, val);
        });

        if (cookieSessionId) {
          sessionStorage.setItem("SessionID", cookieSessionId);
          localStorage.setItem("IsSession", "true");
        }

        localStorage.setItem("CookiesSynced", "true"); // Prevent future resyncs
      }

      refreshLocalStorageData();
      await fetchAndCacheDefaultAvatar();
    } catch (err) {
      console.error("App initialization failed:", err);
    } finally {
      setIsAppReady(true);
    }
  };

useEffect(() => {
  initializeApp();

  const handleStorageChange = () => refreshLocalStorageData();
  window.addEventListener("storage", handleStorageChange);
  return () => window.removeEventListener("storage", handleStorageChange);
}, []);


  useEffect(() => {
    const isSession = localStorage.getItem("IsSession");
    if (isSession) {
      const sessionSessionId = sessionStorage.getItem("SessionID");
      const localSessionId = localStorage.getItem("SessionID");
      if (sessionSessionId !== localSessionId) {
        localStorage.clear();
        sessionStorage.clear();
        toast.error("Session is Expired");
        document.cookie.split(';').forEach(cookie => {
          const cookieName = cookie.split('=')[0].trim();
          document.cookie = `${cookieName}=; path=/; domain=.liaplus.com; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=None`;
        });
        window.location.href = "/login";
      }
    }
  }, []);

  if (!isAppReady) {
    return <Loader />; // Don't render app until cookies are processed
  }

  return (
    <main className="App">
      <Toaster richColors position="top-right" closeButton duration="1500" />
      <CssBaseline />
      <Router>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/assistant/shared/webcall/:uuid" element={<SharedWebCall />} />
            {isLocalhost() ? (
              !token ? (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Registration />} />
                  <Route path="/forgot-password" element={<ForgetPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verifyemail" element={<VerifyEmail />} />
                  <Route path="*" element={<Navigate to="/login" />} />
                </>
              ) : !organizationId ? (
                <Route path="*" element={<CreateCompanyForm />} />
              ) : (
                <Route
                  path="*"
                  element={
                    <SidebarLayout>
                      <AllRoutes userRole={userRole} />
                    </SidebarLayout>
                  }
                />
              )
            ) : !token ? (
              <Route
                path="*"
                element={
                  (() => {
                  window.location.href = `${baseUrl.replace(/\/$/, "")}/auth/login`;
                  return null;
                  })()
                }
              />
            ) : !organizationId ? (
              <Route path="*" element={<CreateCompanyForm />} />
            ) : (
              <Route
                path="*"
                element={
                  <SidebarLayout>
                    <AllRoutes userRole={userRole} />
                  </SidebarLayout>
                }
              />
            )}
          </Routes>
        </Suspense>
      </Router>
    </main>
  );
}

export default App;